import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AuthService } from "../../services/auth.service";
import { ApiService } from "../../services/api.service";
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
  selector: "app-profile",
  templateUrl: "./profile.component.html",
  styleUrls: ["./profile.component.css"],
})
export class ProfileComponent implements OnInit, OnDestroy {
  accountForm: FormGroup;
  activeTab = "account";
  user: any = null;
  orders: any[] = [];
  private userSub: Subscription;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private api: ApiService
  ) {
    this.accountForm = this.fb.group({
      name: [""],
      lastName: [""],
      email: [""],
      phone: [""],
      address: [""],
      complement: [""],
      city: [""],
      departament: [""]
    });
  }

  ngOnInit(): void {
    // 1. Obtener usuario de AuthService / localStorage
    this.userSub = this.authService.currentUser$.subscribe(user => {
      if (user) {
        this.user = user;
        this.populateForm(user);
        this.fetchBackendUserData();
      } else {
        const localUserStr = localStorage.getItem('user');
        if (localUserStr) {
          try {
            this.user = JSON.parse(localUserStr);
            this.populateForm(this.user);
            this.fetchBackendUserData();
          } catch (e) {
            console.error(e);
          }
        }
      }
    });

    // 2. Cargar historial de pedidos reales de la API backend y localStorage
    this.fetchUserOrders();
  }

  fetchUserOrders(): void {
    const userId = this.user?.id || this.user?._id;
    const filter = userId ? { where: { userId: userId } } : {};
    this.api.get<any[]>('transaccions', { filter }).subscribe(
      (res: any) => {
        const backendOrders = Array.isArray(res) ? res : (res?.data || []);
        const localOrdersStr = localStorage.getItem('my_orders');
        const localOrders = localOrdersStr ? JSON.parse(localOrdersStr) : [];
        const combined = [...backendOrders, ...localOrders];
        // Eliminar duplicados por id o cartId
        const unique = combined.filter((v, i, a) => a.findIndex(t => (t.id === v.id || (t.cartId && t.cartId === v.cartId))) === i);
        this.orders = unique.length > 0 ? unique : backendOrders;
      },
      (err) => {
        console.error('Error al cargar órdenes de transaccions:', err);
        const savedOrdersStr = localStorage.getItem('my_orders');
        if (savedOrdersStr) {
          try { this.orders = JSON.parse(savedOrdersStr); } catch { this.orders = []; }
        }
      }
    );
  }

  fetchBackendUserData(): void {
    const userId = this.user?.id || this.user?._id;
    if (!userId) return;

    const filter = {
      include: [
        { relation: 'city' },
        { relation: 'department' }
      ]
    };

    // 1. Consultar directamente las direcciones del usuario en el endpoint público del API
    this.api.get(`users/${userId}/addresses`, { filter }).subscribe(
      (addresses: any) => {
        if (addresses && Array.isArray(addresses) && addresses.length > 0) {
          this.user = {
            ...this.user,
            addresses: addresses
          };
          this.populateForm(this.user);
        }
      },
      () => {
        // Fallback: consulta por filtro en /addresses
        const addressFilter = {
          where: { userId: userId },
          include: [
            { relation: 'city' },
            { relation: 'department' }
          ]
        };
        this.api.get('addresses', { filter: addressFilter }).subscribe(
          (addresses: any) => {
            if (addresses && Array.isArray(addresses) && addresses.length > 0) {
              this.user = {
                ...this.user,
                addresses: addresses
              };
              this.populateForm(this.user);
            }
          }
        );
      }
    );
  }

  extractFullAddress(user: any): { address: string, complement: string, city: string, department: string, phone: string } {
    if (!user) return { address: '', complement: '', city: '', department: '', phone: '' };

    let address = user.address || user.direccion || user.addressLine || '';
    let complement = user.complement || user.complemento || user.neighborhood || user.barrio || '';
    let city = user.city || user.ciudad || '';
    let department = user.department || user.departamento || '';
    let phone = user.phone || user.telefono || user.celular || '';

    if (user.addresses && Array.isArray(user.addresses) && user.addresses.length > 0) {
      const addr = user.addresses[0];
      if (addr.address || addr.direccion) address = addr.address || addr.direccion;
      if (addr.complement || addr.complemento) complement = addr.complement || addr.complemento;
      if (addr.city) {
        city = typeof addr.city === 'object' ? (addr.city.name || addr.city.ciudad || '') : addr.city;
      }
      if (addr.department) {
        department = typeof addr.department === 'object' ? (addr.department.name || addr.department.departamento || '') : addr.department;
      }
      if (addr.phone || addr.telefono) phone = addr.phone || addr.telefono;
    }

    if (typeof city === 'object' && city !== null) city = (city as any).name || (city as any).ciudad || '';
    if (typeof department === 'object' && department !== null) department = (department as any).name || (department as any).departamento || '';

    return { address, complement, city, department, phone };
  }

  populateForm(user: any): void {
    if (!user) return;
    const extracted = this.extractFullAddress(user);

    this.accountForm.patchValue({
      name: user.name || user.fullName || user.nombre || '',
      lastName: user.lastName || user.apellido || '',
      email: user.email || '',
      phone: extracted.phone,
      address: extracted.address,
      complement: extracted.complement,
      city: extracted.city,
      departament: extracted.department
    });
  }

  get userName(): string {
    if (!this.user) return 'Usuario Registrado';
    const name = this.user.name || this.user.fullName || this.user.nombre || '';
    const lastName = this.user.lastName || this.user.apellido || '';
    const fullName = `${name} ${lastName}`.trim();
    return fullName || (this.user.email ? this.user.email.split('@')[0] : 'Usuario Registrado');
  }

  get userEmail(): string {
    return this.user?.email || 'Sin correo asignado';
  }

  get userPhone(): string {
    const extracted = this.extractFullAddress(this.user);
    return extracted.phone || 'No registrado';
  }

  get userLocation(): string {
    const extracted = this.extractFullAddress(this.user);
    if (extracted.city && extracted.department) return `${extracted.city}, ${extracted.department}`;
    if (extracted.city) return extracted.city;
    if (extracted.department) return extracted.department;
    return 'Colombia';
  }

  get userAddress(): string {
    const extracted = this.extractFullAddress(this.user);
    if (!extracted.address) return 'No has registrado una dirección de envío.';
    let full = extracted.address;
    if (extracted.complement) {
      full += ` (${extracted.complement})`;
    }
    return full;
  }

  get userInitials(): string {
    const name = this.userName;
    return name.charAt(0).toUpperCase();
  }

  ngOnDestroy(): void {
    if (this.userSub) {
      this.userSub.unsubscribe();
    }
  }

  setTab(tab: string) {
    this.activeTab = tab;
  }

  onSave(): void {
    const formVals = this.accountForm.value;
    const userId = this.user?.id || this.user?._id;

    const updatedUser = {
      ...this.user,
      name: formVals.name,
      lastName: formVals.lastName,
      email: formVals.email,
      phone: formVals.phone,
      address: formVals.address,
      complement: formVals.complement,
      city: formVals.city,
      department: formVals.departament
    };

    this.user = updatedUser;
    this.authService.updateCurrentUser(updatedUser);

    // Guardar o actualizar la dirección en la BD a través de la API LoopBack
    if (userId && formVals.address) {
      const addressPayload: any = {
        address: formVals.address,
        complement: formVals.complement || '',
        userId: userId
      };

      const existingAddrId = (this.user.addresses && this.user.addresses[0]) ? this.user.addresses[0].id : null;

      if (existingAddrId) {
        this.api.patch(`addresses/${existingAddrId}`, addressPayload).subscribe(
          () => console.log('Dirección actualizada con éxito en la BD'),
          (err) => console.error('Error al actualizar dirección en la BD:', err)
        );
      } else {
        this.api.post(`users/${userId}/addresses`, addressPayload).subscribe(
          (newAddr: any) => {
            if (newAddr) {
              this.user.addresses = [newAddr];
              this.authService.updateCurrentUser(this.user);
            }
          },
          (err) => console.error('Error al guardar dirección en la BD:', err)
        );
      }
    }

    Swal.fire({
      icon: 'success',
      title: '¡Perfil Actualizado!',
      text: 'Tu información personal y dirección se han guardado con éxito en tu cuenta y base de datos.',
      confirmButtonColor: '#4f46e5'
    });
  }
}

